/**
 * @file components/search/SearchView.jsx
 * Uses useConversations and useChatSession.
 * Redesigned: minimalist list, 3-row cards, sort, collapsible date groups, stats, rename/delete.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useConversations } from '../../context/ConversationsContext';
import { useChatSession } from '../../context/ChatSessionContext';
import ConversationActions from '../sidebar/ConversationActions';
import styles from '../../styles/ChatApp.module.css';
import actionStyles from '../../styles/ConversationActions.module.css';
import { MessageSquare, Clock, Paperclip, Search, X, ChevronDown } from 'lucide-react';

// --- Helpers ---

/** Format an ISO string as "25 Feb 2026" */
const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

/** Group an array of conversations into Today/Yesterday/This Week/Older buckets */
const groupByDate = (conversations) => {
    const groups = { today: [], yesterday: [], thisWeek: [], older: [] };
    const now       = new Date();
    const today     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo   = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);

    conversations.forEach(conv => {
        const d = new Date(conv.createdAt || conv.lastUpdated);
        if      (d >= today)     groups.today.push(conv);
        else if (d >= yesterday) groups.yesterday.push(conv);
        else if (d >= weekAgo)   groups.thisWeek.push(conv);
        else                     groups.older.push(conv);
    });
    return groups;
};

/** Wrap the matched search term in a <mark> span, returns React nodes */
const highlightText = (text, term) => {
    if (!term.trim() || !text) return text;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase()
            ? <mark key={i} className={styles.searchHighlight}>{part}</mark>
            : part
    );
};

const SORT_OPTIONS = [
    { key: 'newest', label: 'Newest' },
    { key: 'oldest', label: 'Oldest' },
    { key: 'az',     label: 'A–Z' },
    { key: 'za',     label: 'Z–A' },
];

const GROUP_SECTIONS = [
    { key: 'today',     label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek',  label: 'This Week' },
    { key: 'older',     label: 'Older' },
];

// --- Component ---

const SearchView = () => {
    const { conversations, handleRenameConversation, refreshConversations, isLoadingConversations } = useConversations();
    const { loadConversationInChat } = useChatSession();

    const [searchTerm,      setSearchTerm]      = useState('');
    const [sortOrder,       setSortOrder]       = useState('newest');
    const [hoveredId,       setHoveredId]       = useState(null);
    const [editingId,       setEditingId]       = useState(null);
    const [editingTitle,    setEditingTitle]    = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const searchInputRef = useRef(null);
    const renameInputRef = useRef(null);

    // Fetch fresh data from the API every time the search view opens,
    // so message counts and previews are always up to date.
    useEffect(() => {
        refreshConversations();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-focus search on mount — desktop only.
    // On touch devices (pointer: coarse) focusing immediately opens the virtual
    // keyboard, shrinking the viewport and hiding the results list behind it.
    useEffect(() => {
        if (window.matchMedia('(pointer: coarse)').matches) return;
        const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
        return () => clearTimeout(timer);
    }, []);

    // Focus rename input when editing starts
    useEffect(() => {
        if (editingId && renameInputRef.current) {
            renameInputRef.current.focus();
            renameInputRef.current.select();
        }
    }, [editingId]);

    // --- Group toggle ---
    const toggleGroup = (key) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // --- Reset all filters, sort, and group state ---
    const isDirty = searchTerm !== '' || sortOrder !== 'newest';

    const handleReset = () => {
        setSearchTerm('');
        setSortOrder('newest');
        setCollapsedGroups(new Set());
        setTimeout(() => searchInputRef.current?.focus(), 0);
    };

    // --- Rename handlers (mirrors RecentChats pattern) ---
    const handleStartRename = (id, currentTitle) => {
        setEditingId(id);
        setEditingTitle(currentTitle || 'Untitled Chat');
        setHoveredId(null);
    };

    const handleCancelRename = () => {
        setEditingId(null);
        setEditingTitle('');
    };

    const handleSaveRename = async () => {
        const id = editingId;
        const original = conversations.find(c => c.id === id);
        if (!id || !editingTitle.trim() || editingTitle.trim() === original?.title) {
            handleCancelRename();
            return;
        }
        try {
            await handleRenameConversation(id, editingTitle.trim());
        } catch (err) {
            console.error('SearchView: Failed to rename:', err);
        } finally {
            handleCancelRename();
        }
    };

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter')  handleSaveRename();
        if (e.key === 'Escape') handleCancelRename();
    };

    // --- Filter + sort ---
    const filteredAndSorted = useMemo(() => {
        let result = [...conversations];

        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(c =>
                c.title?.toLowerCase().includes(lower) ||
                c.firstAssistantPreview?.toLowerCase().includes(lower)
            );
        }

        switch (sortOrder) {
            case 'oldest':
                result.sort((a, b) =>
                    new Date(a.createdAt || a.lastUpdated) - new Date(b.createdAt || b.lastUpdated)
                );
                break;
            case 'az':
                result.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                break;
            case 'za':
                result.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
                break;
            default: // newest
                result.sort((a, b) =>
                    new Date(b.createdAt || b.lastUpdated) - new Date(a.createdAt || a.lastUpdated)
                );
        }

        return result;
    }, [conversations, searchTerm, sortOrder]);

    // Groups are shown only when idle (no search) AND sort is "newest"
    const isSearching = searchTerm.trim().length > 0;
    const isGrouped   = !isSearching && sortOrder === 'newest';

    // --- Single conversation row ---
    const renderRow = (convo) => {
        const isEditing = editingId === convo.id;
        const isHovered = hoveredId === convo.id;

        return (
            <div
                key={convo.id}
                className={styles.searchResultItem}
                onClick={!isEditing ? () => loadConversationInChat(convo.id) : undefined}
                onMouseEnter={() => !isEditing && setHoveredId(convo.id)}
                onMouseLeave={() => setHoveredId(null)}
                role="button"
                tabIndex="0"
                onKeyDown={(e) =>
                    !isEditing && (e.key === 'Enter' || e.key === ' ') &&
                    loadConversationInChat(convo.id)
                }
            >
                <div className={styles.searchResultContent}>

                    {/* Row 1 — title + optional "..." menu */}
                    <div className={styles.searchResultRow1}>
                        {isEditing ? (
                            <input
                                ref={renameInputRef}
                                type="text"
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onKeyDown={handleRenameKeyDown}
                                onBlur={handleSaveRename}
                                onClick={(e) => e.stopPropagation()}
                                className={actionStyles.renameInput}
                            />
                        ) : (
                            <p className={styles.searchResultTitle}>
                                {highlightText(convo.title || 'Untitled Chat', searchTerm)}
                            </p>
                        )}

                        {isHovered && !isEditing && (
                            <div onClick={(e) => e.stopPropagation()}>
                                <ConversationActions
                                    conversationId={convo.id}
                                    currentTitle={convo.title || 'Untitled Chat'}
                                    onStartRename={() => handleStartRename(convo.id, convo.title)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Row 2 — first assistant preview (hidden if empty) */}
                    {convo.firstAssistantPreview && (
                        <p className={styles.searchResultPreview}>
                            {convo.firstAssistantPreview}
                        </p>
                    )}

                    {/* Row 3 — date + stats */}
                    <div className={styles.searchResultMeta}>
                        <span className={styles.searchResultDate}>
                            <Clock size={11} />
                            {formatDate(convo.createdAt || convo.lastUpdated)}
                        </span>
                        <div className={styles.searchResultStats}>
                            {convo.messageCount > 0 && (
                                <span className={styles.statChip}>
                                    <MessageSquare size={11} />
                                    {convo.messageCount}
                                </span>
                            )}
                            {convo.fileCount > 0 && (
                                <span className={styles.statChip}>
                                    <Paperclip size={11} />
                                    {convo.fileCount}
                                </span>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    // --- Grouped list: only rendered when isGrouped === true ---
    const renderGrouped = () => {
        const groups = groupByDate(filteredAndSorted);
        return GROUP_SECTIONS
            .filter(s => groups[s.key].length > 0)
            .map(s => {
                const isCollapsed = collapsedGroups.has(s.key);
                return (
                    <div key={s.key}>
                        <div
                            className={styles.searchGroupHeader}
                            onClick={() => toggleGroup(s.key)}
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) =>
                                (e.key === 'Enter' || e.key === ' ') && toggleGroup(s.key)
                            }
                            aria-expanded={!isCollapsed}
                        >
                            <span>{s.label}</span>
                            <div className={styles.searchGroupMeta}>
                                <span className={styles.searchGroupCount}>
                                    {groups[s.key].length}
                                </span>
                                <ChevronDown
                                    size={12}
                                    className={`${styles.searchGroupChevron} ${isCollapsed ? styles.searchGroupChevronCollapsed : ''}`}
                                />
                            </div>
                        </div>
                        {!isCollapsed && groups[s.key].map(renderRow)}
                    </div>
                );
            });
    };

    // --- Render ---
    return (
        <div className={styles.searchViewContainer}>
            <h2 className={styles.searchViewTitle}>Search</h2>

            {/* Search input with leading icon + clear button */}
            <div className={styles.searchInputWrapper}>
                <Search size={16} className={styles.searchInputIcon} />
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                    aria-label="Search conversations"
                />
                {isDirty && (
                    <button
                        className={styles.searchInputClear}
                        onClick={handleReset}
                        title="Clear search and reset filters"
                        aria-label="Clear search"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Sort controls + status indicator */}
            <div className={styles.searchControls}>
                <div className={styles.sortButtons}>
                    {SORT_OPTIONS.map(opt => (
                        <button
                            key={opt.key}
                            className={`${styles.sortButton} ${sortOrder === opt.key ? styles.sortButtonActive : ''}`}
                            onClick={() => setSortOrder(opt.key)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                {isLoadingConversations && (
                    <span className={styles.resultCount}>Refreshing…</span>
                )}
                {!isLoadingConversations && isSearching && (
                    <span className={styles.resultCount}>
                        {filteredAndSorted.length}{' '}
                        {filteredAndSorted.length === 1 ? 'result' : 'results'}
                    </span>
                )}
            </div>

            {/* Results */}
            <div className={styles.searchResults}>
                {filteredAndSorted.length > 0 ? (
                    isGrouped
                        ? renderGrouped()
                        : filteredAndSorted.map(renderRow)
                ) : (
                    <div className={styles.searchEmptyState}>
                        <Search size={32} className={styles.searchEmptyIcon} />
                        <p>
                            {isSearching
                                ? 'No conversations match your search.'
                                : 'No conversations yet.'}
                        </p>
                        {isSearching && <span>Try a different search term.</span>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchView;
